import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";

// Hook to manage table cell editing
export const useTableDataEditor = (tableName, tableData, filteredTableData) => {
    const [editingCell, setEditingCell] = useState(null);
    const [editValue, setEditValue] = useState("");
    const editInputRef = useRef(null);
    
    // Handle cell click to enter edit mode
    const handleCellClick = (rowIndex, columnName, value) => {
        setEditingCell({ rowIndex, columnName });
        setEditValue(value !== null ? String(value) : "");
    };
    
    // Handle save of edited cell
    const handleSaveEdit = async () => {
        if (!editingCell || !tableName) return;
        
        const { rowIndex, columnName } = editingCell;
        const rowData = filteredTableData[rowIndex];
        
        // Need primary key to update the specific row
        const primaryKey = Object.keys(rowData).find(key => 
            key.toLowerCase() === 'id' || 
            key.toLowerCase().includes('_id') || 
            key.toLowerCase() === 'key' ||
            key.toLowerCase() === `${tableName.toLowerCase()}_id`
        );
        
        if (!primaryKey) {
            alert("Cannot update: No primary key found for this table");
            setEditingCell(null);
            return;
        }
        
        try {
            // Construct the UPDATE SQL query
            const query = `UPDATE ${tableName} SET ${columnName} = ? WHERE ${primaryKey} = ?`;
            const params = [editValue, rowData[primaryKey]];
            
            // Send the parameterized query
            const response = await axios.post("/query", {
                query,
                params
            }, {
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${localStorage.getItem('authToken')}`
                }
            });
            
            if (response.data.isSuccess) {
                // Update the local data
                const newData = [...tableData];
                const dataIndex = tableData.findIndex(row => row[primaryKey] === rowData[primaryKey]);
                if (dataIndex !== -1) {
                    newData[dataIndex] = { ...newData[dataIndex], [columnName]: editValue };
                    // We need to update tableData here
                    // This will be passed back to the component and then filteredTableData can be updated
                }
                
                // Exit edit mode
                setEditingCell(null);
            } else {
                alert(`Failed to update: ${response.data.message}`);
            }
        } catch (error) {
            alert(`Error updating cell: ${error.response?.data?.message || error.message}`);
        }
    };
    
    // Handle cancel edit
    const handleCancelEdit = useCallback(() => {
        setEditingCell(null);
    }, []);
    
    // Handle keypress in edit mode
    const handleEditKeyPress = (e) => {
        if (e.key === 'Enter') {
            handleSaveEdit();
        } else if (e.key === 'Escape') {
            handleCancelEdit();
        }
    };
    
    // Handle click outside to cancel edit
    useEffect(() => {
        if (editingCell) {
            const handleClickOutside = (event) => {
                if (editInputRef.current && !editInputRef.current.contains(event.target)) {
                    handleCancelEdit();
                }
            };
            
            document.addEventListener('mousedown', handleClickOutside);
            return () => {
                document.removeEventListener('mousedown', handleClickOutside);
            };
        }
    }, [editingCell, handleCancelEdit]);
    
    return {
        editingCell,
        editValue,
        setEditValue,
        editInputRef,
        handleCellClick,
        handleSaveEdit,
        handleCancelEdit,
        handleEditKeyPress
    };
};